import PropTypes from 'prop-types'
import classNames from 'classnames'
import { useTranslation } from 'react-i18next'

import ItemFooter from './ItemFooter'

import classes from './QuizResults.module.scss'

const QuizResults = ({ quiz, attempt, canRetake, onRetake, onPrevious, onNext, hasNext }) => {
  const { t } = useTranslation()
  const nextLabel = t(hasNext ? 'portal.certification.quiz.continue' : 'portal.certification.item.next.finish')
  const correctCount = attempt.results.filter(result => result.correct).length
  const answerText = (question, answerId) => question.answers.find(answer => answer.id === answerId)?.text

  return (
    <div>
      <div className={classNames('d-flex align-items-center', classes.summary)}>
        <i
          className={classNames(attempt.passed ? 'fa fa-check-circle' : 'fa fa-refresh', classes.icon, { [classes.iconPassed]: attempt.passed })}
          aria-hidden='true'
        />
        <div>
          <div className={classNames(classes.score, { [classes.scorePassed]: attempt.passed })}>{attempt.score}%</div>
          <div className={classes.line}>
            {t(attempt.passed ? 'portal.certification.quiz.passed' : 'portal.certification.quiz.notPassed', { passMark: quiz.passMark })}
            {' '}
            {t('portal.certification.quiz.correctCount', { correct: correctCount, total: attempt.results.length })}
          </div>
        </div>
      </div>

      <ol className={classNames('list-unstyled mb-0 d-flex flex-column', classes.review)}>
        {attempt.results.map((result, index) => {
          const question = quiz.questions.find(current => current.id === result.questionId)
          if (!question) { return null }

          return (
            <li
              key={result.questionId}
              className={classNames(classes.reviewItem, result.correct ? classes.reviewCorrect : classes.reviewIncorrect)}
            >
              <div className={classNames('d-flex align-items-start', classes.reviewHead)}>
                <div className={classes.reviewPrompt}>{index + 1}. {question.prompt}</div>
                <span className={classes.reviewStatus}>
                  <i className={result.correct ? 'fa fa-check' : 'fa fa-times'} aria-hidden='true' />
                  {t(result.correct ? 'portal.certification.quiz.correct' : 'portal.certification.quiz.incorrect')}
                </span>
              </div>
              <div className={classes.reviewAnswer}>
                {t('portal.certification.quiz.yourAnswer', {
                  text: answerText(question, result.answerId) || t('portal.certification.quiz.unanswered'),
                })}
              </div>
              {!result.correct && (
                <div className={classes.correctAnswer}>
                  {t('portal.certification.quiz.correctAnswer', { text: answerText(question, result.correctAnswerId) })}
                </div>
              )}
              {result.explanation && <div className={classes.explanation}>{result.explanation}</div>}
            </li>
          )
        })}
      </ol>

      <ItemFooter
        onPrevious={onPrevious}
        nextLabel={attempt.passed ? nextLabel : (canRetake ? t('portal.certification.quiz.retake') : undefined)}
        onNext={attempt.passed ? onNext : onRetake}
      />
    </div>
  )
}

QuizResults.propTypes = {
  quiz: PropTypes.shape({
    passMark: PropTypes.number.isRequired,
    questions: PropTypes.array.isRequired,
  }).isRequired,
  attempt: PropTypes.shape({
    score: PropTypes.number.isRequired,
    passed: PropTypes.bool.isRequired,
    results: PropTypes.arrayOf(PropTypes.shape({
      questionId: PropTypes.number.isRequired,
      answerId: PropTypes.number,
      correctAnswerId: PropTypes.number,
      correct: PropTypes.bool.isRequired,
      explanation: PropTypes.string,
    })).isRequired,
  }).isRequired,
  canRetake: PropTypes.bool.isRequired,
  onRetake: PropTypes.func.isRequired,
  onPrevious: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  hasNext: PropTypes.bool.isRequired,
}

export default QuizResults
